import type { AuthProvider } from '@refinedev/core';
import type { MeResponse } from '@verybigthings/trackframe-shared/schemas';
import { hub, hubBaseUrl } from './hub-client';

interface LoginVars {
  email: string;
  password: string;
}

interface BootstrapVars extends LoginVars {
  token: string;
  name?: string;
}

function currentPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

function isPublicAuthPath(pathname = currentPathname()): boolean {
  return pathname === '/login'
    || pathname.startsWith('/login/')
    || pathname === '/bootstrap'
    || pathname.startsWith('/bootstrap/');
}

// Send unauthenticated app routes through the hub's /login entry so return_to
// is validated server-side before the portal sends the user back.
function gotoSsoPortal(): boolean {
  if (typeof window === 'undefined') return false;
  const here = window.location.pathname + window.location.search;
  // Avoid a hard reload loop on the public auth entry pages.
  if (isPublicAuthPath()) return false;
  const returnTo = window.location.origin + here;
  const base = hubBaseUrl || '';
  window.location.href = `${base}/login?return_to=${encodeURIComponent(returnTo)}`;
  return true;
}

let ssoRedirectInFlight = false;
function bounceToPortalOnce(): void {
  if (isPublicAuthPath()) return;
  if (ssoRedirectInFlight) return;
  ssoRedirectInFlight = gotoSsoPortal();
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }: LoginVars) => {
    try {
      // Cookies are set by the server on this response. No client-side
      // token-store wiring needed — subsequent calls auto-attach them.
      await hub.authLogin({ body: { email, password } });
      return { success: true, redirectTo: '/' };
    } catch (err) {
      return {
        success: false,
        error: { name: 'LoginFailed', message: (err as Error).message ?? 'Invalid credentials' },
      };
    }
  },

  register: async ({ email, password, token, name }: BootstrapVars) => {
    try {
      await hub.authBootstrapAdmin({
        body: { email, password, token, ...(name ? { name } : {}) },
      });
      return { success: true, redirectTo: '/' };
    } catch (err) {
      return {
        success: false,
        error: { name: 'BootstrapFailed', message: (err as Error).message ?? 'Bootstrap failed' },
      };
    }
  },

  logout: async () => {
    // Top-level navigation to the hub's SSO logout. The hub clears tf_sso +
    // tf_refresh cookies on the response (matching Domain attribute) and
    // 302s back to /login. No XHR-based revoke: the cookies are the
    // session, and only a hub-origin response can delete them.
    if (typeof window !== 'undefined') {
      const base = hubBaseUrl || '';
      const here = window.location.origin + window.location.pathname + window.location.search;
      window.location.href = `${base}/auth/sso/logout?return_to=${encodeURIComponent(here)}`;
    }
    return { success: true };
  },

  check: async () => {
    if (isPublicAuthPath()) {
      return { authenticated: false };
    }
    try {
      // Cookie auto-attached by the browser. If the JWT cookie is missing /
      // expired, the hub returns 401 and we bounce to the portal.
      await hub.authMe();
      return { authenticated: true };
    } catch {
      bounceToPortalOnce();
      return { authenticated: false };
    }
  },

  getIdentity: async () => {
    try {
      const me: MeResponse = await hub.authMe();
      return me.user;
    } catch {
      return null;
    }
  },

  getPermissions: async () => {
    try {
      const me: MeResponse = await hub.authMe();
      return me.user.roles;
    } catch {
      return [];
    }
  },

  onError: async (error) => {
    const status = (error as { statusCode?: number; status?: number })?.statusCode
      ?? (error as { statusCode?: number; status?: number })?.status;
    const message = (error as Error)?.message ?? '';
    const is401 = status === 401 || /\b401\b/.test(message);
    const is403 = status === 403 || /\b403\b/.test(message);
    if (is401) {
      // On public auth pages the form is already showing, so there is no
      // stale app session to clear. Returning `logout: true` here makes
      // Refine call authProvider.logout(), which navigates through
      // /auth/sso/logout and lands back on an auth page, recreating the
      // same 401 cycle.
      if (isPublicAuthPath()) {
        return { error };
      }
      return { logout: true, error };
    }
    if (is403) {
      // Authenticated but forbidden — surface the error rather than looping
      // through SSO (which would re-grant the same identity → same 403).
      return { error };
    }
    return { error };
  },
};
